/**
 * Deterministic identifiers (SPEC §2 `core/ids.ts`).
 *
 * Every id here is **derived from what it names**, not drawn from the PRNG. SPEC sketches
 * "deterministic id generation from the injected Rng", and a seeded stream would indeed be
 * reproducible — but reproducible is weaker than what §10 actually needs.
 *
 * §10 requires that "writes are idempotent and keyed by attempt id so a mid-write reload cannot
 * double-count". With a random id, submitting the same draw twice after an interrupted write
 * produces two ids and two rows, and idempotence depends on nobody ever retrying. With a derived
 * id it is structural: the same draw is the same key, so the second write overwrites the first and
 * the count cannot move. See DECISIONS D-106.
 */

/** One run. Numbered within the session; the seed makes two sessions distinguishable in an export. */
export function runId(seed: number, counter: number): string {
  return `run-${seed}-${counter}`;
}

/**
 * One attempt: this run, this position in the draw sequence, this item.
 *
 * The draw index is in the key, so an item drawn twice in one run (a repeat, which never advances
 * the interval) is two distinct attempts, while a re-submitted draw is one.
 */
export function attemptId(run: string, drawIndex: number, itemId: string): string {
  return `${run}/${String(drawIndex).padStart(4, '0')}/${itemId}`;
}
